import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, resolveComponent as _resolveComponent, createElementVNode as _createElementVNode, Fragment as _Fragment, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, renderList as _renderList, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "quickpay-block-preferences__empty-state" }
const _hoisted_2 = { class: "quickpay-block-preferences__section-title typographies-heading-h3" }
const _hoisted_3 = { class: "quickpay-block-preferences__section-description typographies-paragraph-medium-regular" }
const _hoisted_4 = { class: "quickpay-block-preferences__section-title typographies-heading-h3" }
const _hoisted_5 = { class: "quickpay-block-preferences__section-description typographies-paragraph-medium-regular" }

import { ref, computed, watch, inject, onMounted } from 'vue';
import { Translator } from 'types/injects/translator.type';
import { PushToast } from 'types/injects/pushToast.type';
import Card from 'vcomponents/common/card/Card.vue';
import Heading from 'vcomponents/common/Heading/Heading.vue';
import QuickpaySettingsBlockPreferencesLoader from 'vcomponents/quickpay/settings/QuickpaySettingsBlockPreferencesLoader.vue';
import { fetchPaymentPreferences, savePaymentPreferences } from 'api/quickpay/quickpayService';

interface Props {
    providerStatuses: Record<string, string>;
    isLoadingProviders?: boolean;
}

interface OnlineProvider {
    id: string;
    label: string;
    caption: string;
}

interface DirectDebitProvider {
    id: string;
    label: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'QuickpaySettingsBlockPreferences',
  props: {
    providerStatuses: {},
    isLoadingProviders: { type: Boolean }
  },
  setup(__props: any) {

const props = __props;

const $t = inject('$t') as Translator;
const $pushToast = inject('$pushToast') as PushToast;

const ONLINE_PAYMENT_PROVIDER_IDS = ['stancer', 'stripe', 'paypal', 'adyen', 'atos'];
const DIRECT_DEBIT_PROVIDER_IDS = ['gocardless', 'stripe'];

const PROVIDER_CONFIG: Record<
    string,
    {
        onlineLabelKey?: string;
        onlineCaptionKey?: string;
        directDebitLabelKey?: string;
    }
> = {
    stancer: {
        onlineLabelKey: 'quickpay.settings.preferences.providers.stancer.label',
        onlineCaptionKey: 'quickpay.settings.preferences.providers.stancer.caption',
    },
    stripe: {
        onlineLabelKey: 'quickpay.settings.preferences.providers.stripe.label',
        onlineCaptionKey: 'quickpay.settings.preferences.providers.stripe.caption',
        directDebitLabelKey: 'quickpay.settings.preferences.providers.stripe.directDebit.label',
    },
    paypal: {
        onlineLabelKey: 'quickpay.settings.preferences.providers.paypal.label',
        onlineCaptionKey: 'quickpay.settings.preferences.providers.paypal.caption',
    },
    adyen: {
        onlineLabelKey: 'quickpay.settings.preferences.providers.adyen.label',
        onlineCaptionKey: 'quickpay.settings.preferences.providers.adyen.caption',
    },
    atos: {
        onlineLabelKey: 'quickpay.settings.preferences.providers.atos.label',
        onlineCaptionKey: 'quickpay.settings.preferences.providers.atos.caption',
    },
    gocardless: {
        directDebitLabelKey: 'quickpay.settings.preferences.providers.gocardless.directDebit.label',
    },
};

const translations = {
    title: $t.__('quickpay.settings.preferences.title'),
    emptyStateDescription: $t.__('quickpay.settings.preferences.emptyState.description'),
    immediatePaymentTitle: $t.__('quickpay.settings.preferences.immediatePayment.title'),
    immediatePaymentDescription: $t.__(
        'quickpay.settings.preferences.immediatePayment.description',
    ),
    recurringPaymentTitle: $t.__('quickpay.settings.preferences.recurringPayment.title'),
    recurringPaymentDescription: $t.__(
        'quickpay.settings.preferences.recurringPayment.description',
    ),
    recurringPaymentNone: $t.__('quickpay.settings.preferences.recurringPayment.none'),
    alert: $t.__('quickpay.settings.preferences.alert'),
};

const isLoadingPreferences = ref(true);
const isInitialized = ref(false);
const isSaving = ref(false);
const selectedOnlinePayments = ref<string[]>([]);
const selectedDirectDebit = ref<string>('none');

const connectedProviderIds = computed((): string[] => {
    return Object.entries(props.providerStatuses)
        .filter(([, status]) => status === 'connected')
        .map(([id]) => id);
});

// Connected providers that support immediate (online) payment, with translated labels
const onlineProviders = computed((): OnlineProvider[] => {
    return ONLINE_PAYMENT_PROVIDER_IDS.flatMap((id) => {
        const config = PROVIDER_CONFIG[id];
        if (
            !connectedProviderIds.value.includes(id) ||
            !config?.onlineLabelKey ||
            !config?.onlineCaptionKey
        ) {
            return [];
        }
        return {
            id,
            label: $t.__(config.onlineLabelKey),
            caption: $t.__(config.onlineCaptionKey),
        };
    });
});

// Connected providers that support recurring (direct debit) payment, with translated labels
const directDebitProviders = computed((): DirectDebitProvider[] => {
    return DIRECT_DEBIT_PROVIDER_IDS.flatMap((id) => {
        const config = PROVIDER_CONFIG[id];
        if (!connectedProviderIds.value.includes(id) || !config?.directDebitLabelKey) {
            return [];
        }
        return {
            id,
            label: $t.__(config.directDebitLabelKey),
        };
    });
});

const hasOnlineProviders = computed((): boolean => onlineProviders.value.length > 0);

const hasDirectDebitProviders = computed((): boolean => directDebitProviders.value.length > 0);

const hasConnectedProviders = computed(
    (): boolean => hasOnlineProviders.value || hasDirectDebitProviders.value,
);

const loadPreferences = async (): Promise<void> => {
    try {
        const response = await fetchPaymentPreferences();
        const data = response.data as { onlinePayments: string[]; directDebit: string | false };
        selectedOnlinePayments.value = data.onlinePayments ?? [];
        selectedDirectDebit.value = data.directDebit || 'none';
    } catch {
        $pushToast({
            type: 'error',
            icon: true,
            text: $t.__('common.labels.toast.error'),
        });
    } finally {
        isLoadingPreferences.value = false;
        isInitialized.value = true;
    }
};

const save = async (): Promise<void> => {
    isSaving.value = true;
    try {
        await savePaymentPreferences({
            onlinePayments: selectedOnlinePayments.value,
            directDebit: selectedDirectDebit.value === 'none' ? false : selectedDirectDebit.value,
        });
    } catch {
        $pushToast({
            type: 'error',
            icon: true,
            text: $t.__('common.labels.toast.error'),
        });
    } finally {
        isSaving.value = false;
    }
};

watch(
    selectedOnlinePayments,
    () => {
        if (isInitialized.value) {
            save();
        }
    },
    { deep: true },
);
watch(selectedDirectDebit, () => {
    if (isInitialized.value) {
        save();
    }
});

watch(hasConnectedProviders, (nowHasProviders) => {
    if (nowHasProviders && !isInitialized.value) {
        isLoadingPreferences.value = true;
        loadPreferences();
    }
});

onMounted(() => {
    if (hasConnectedProviders.value) {
        loadPreferences();
    } else {
        isLoadingPreferences.value = false;
    }
});

return (_ctx: any,_cache: any) => {
  const _component_CompassEmptyState = _resolveComponent("CompassEmptyState")!
  const _component_CompassCheckbox = _resolveComponent("CompassCheckbox")!
  const _component_CompassCheckboxGroup = _resolveComponent("CompassCheckboxGroup")!
  const _component_CompassRadio = _resolveComponent("CompassRadio")!
  const _component_CompassRadioGroup = _resolveComponent("CompassRadioGroup")!
  const _component_CompassAlert = _resolveComponent("CompassAlert")!

  return (_openBlock(), _createBlock(Card, { class: "quickpay-block-preferences" }, {
    default: _withCtx(() => [
      (props.isLoadingProviders || (hasConnectedProviders.value && isLoadingPreferences.value))
        ? (_openBlock(), _createBlock(QuickpaySettingsBlockPreferencesLoader, { key: 0 }))
        : (!hasConnectedProviders.value)
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
              _createVNode(Heading, {
                labelTitle: translations.title,
                variant: "h2",
                prefixColoredIcon: { icon: 'adjustments-horizontal', color: 'brand' }
              }, null, 8 /* PROPS */, ["labelTitle"]),
              _createElementVNode("div", _hoisted_1, [
                _createVNode(_component_CompassEmptyState, {
                  illustrationPath: "/ressources/logged/img/illustrations/payments-empty.svg",
                  caption: translations.emptyStateDescription
                }, null, 8 /* PROPS */, ["caption"])
              ])
            ], 64 /* STABLE_FRAGMENT */))
          : (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
              _createVNode(Heading, {
                labelTitle: translations.title,
                variant: "h2",
                prefixColoredIcon: { icon: 'adjustments-horizontal', color: 'brand' }
              }, null, 8 /* PROPS */, ["labelTitle"]),
              (hasOnlineProviders.value)
                ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                    _createElementVNode("h3", _hoisted_2, _toDisplayString(translations.immediatePaymentTitle), 1 /* TEXT */),
                    _createElementVNode("p", _hoisted_3, _toDisplayString(translations.immediatePaymentDescription), 1 /* TEXT */),
                    _createVNode(_component_CompassCheckboxGroup, {
                      modelValue: selectedOnlinePayments.value,
                      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((selectedOnlinePayments).value = $event)),
                      class: "quickpay-block-preferences__choices",
                      disabled: isSaving.value
                    }, {
                      default: _withCtx(() => [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(onlineProviders.value, (provider) => {
                          return (_openBlock(), _createBlock(_component_CompassCheckbox, {
                            key: provider.id,
                            modelValue: provider.id,
                            label: provider.label,
                            caption: provider.caption,
                            disabled: isSaving.value
                          }, null, 8 /* PROPS */, ["modelValue", "label", "caption", "disabled"]))
                        }), 128 /* KEYED_FRAGMENT */))
                      ]),
                      _: 1 /* STABLE */
                    }, 8 /* PROPS */, ["modelValue", "disabled"])
                  ], 64 /* STABLE_FRAGMENT */))
                : _createCommentVNode("v-if", true),
              (hasDirectDebitProviders.value)
                ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                    _createElementVNode("h3", _hoisted_4, _toDisplayString(translations.recurringPaymentTitle), 1 /* TEXT */),
                    _createElementVNode("p", _hoisted_5, _toDisplayString(translations.recurringPaymentDescription), 1 /* TEXT */),
                    _createVNode(_component_CompassRadioGroup, {
                      modelValue: selectedDirectDebit.value,
                      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((selectedDirectDebit).value = $event)),
                      class: "quickpay-block-preferences__choices",
                      direction: "vertical",
                      disabled: isSaving.value
                    }, {
                      default: _withCtx(() => [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(directDebitProviders.value, (provider) => {
                          return (_openBlock(), _createBlock(_component_CompassRadio, {
                            key: provider.id,
                            modelValue: provider.id,
                            label: provider.label,
                            disabled: isSaving.value
                          }, null, 8 /* PROPS */, ["modelValue", "label", "disabled"]))
                        }), 128 /* KEYED_FRAGMENT */)),
                        _createVNode(_component_CompassRadio, {
                          modelValue: "none",
                          label: translations.recurringPaymentNone,
                          disabled: isSaving.value
                        }, null, 8 /* PROPS */, ["label", "disabled"])
                      ]),
                      _: 1 /* STABLE */
                    }, 8 /* PROPS */, ["modelValue", "disabled"])
                  ], 64 /* STABLE_FRAGMENT */))
                : _createCommentVNode("v-if", true),
              _createVNode(_component_CompassAlert, {
                class: "quickpay-block-preferences__alert",
                type: _ctx.CompassEnums.AlertType.NEUTRAL,
                size: _ctx.CompassEnums.AlertSize.SMALL,
                description: translations.alert,
                showIcon: false,
                showClose: false
              }, null, 8 /* PROPS */, ["type", "size", "description"])
            ], 64 /* STABLE_FRAGMENT */))
    ]),
    _: 1 /* STABLE */
  }))
}
}

})